import { useRef, useState } from "react";
import { toast } from "sonner";
import { Star, Trash2, Upload } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useBlobImage } from "@/lib/useBlobImage";

export function AdminImage({ imageId, url, alt, className }) {
  const blob = useBlobImage(api, imageId ? `/admin/product-images/${imageId}` : null);
  const [err, setErr] = useState(false);
  const src = blob || (!imageId && url);
  if (!src || err) return <div className={`flex items-center justify-center border border-border bg-secondary text-[10px] font-bold text-primary-foreground/70 ${className}`}>ATL</div>;
  return <img src={src} alt={alt} onError={() => setErr(true)} className={`border border-border object-cover ${className}`} />;
}

export default function ProductImages({ product, onChange }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const images = product.images || [];

  const upload = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    setBusy(true);
    try { onChange((await api.post(`/admin/products/${product.id}/images`, fd)).data); toast.success(`${files.length} image(s) uploaded`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  const setMain = async (iid) => onChange((await api.put(`/admin/products/${product.id}/images/${iid}/main`)).data);
  const remove = async (iid) => {
    if (!window.confirm("Remove this image?")) return;
    onChange((await api.delete(`/admin/products/${product.id}/images/${iid}`)).data);
  };

  return (
    <div className="space-y-2" data-testid="product-images-manager">
      <div className="grid grid-cols-4 gap-2">
        {images.map((img) => {
          const main = product.main_image_id === img.id;
          return (
            <div key={img.id} className={`group relative overflow-hidden rounded-lg border-2 ${main ? "border-primary" : "border-transparent"}`} data-testid={`product-image-${img.id}`}>
              <AdminImage imageId={img.id} alt="" className="aspect-square w-full rounded-md" />
              {main && <span className="absolute left-1 top-1 rounded bg-primary px-1.5 py-0.5 text-[10px] font-bold" data-testid={`product-image-main-badge-${img.id}`}>MAIN</span>}
              <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 bg-white/90 py-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100">
                {!main && <button type="button" onClick={() => setMain(img.id)} title="Make main image" className="rounded p-1 hover:bg-secondary" data-testid={`product-image-set-main-${img.id}`}><Star className="h-3.5 w-3.5" /></button>}
                <button type="button" onClick={() => remove(img.id)} title="Remove image" className="rounded p-1 text-rose-600 hover:bg-rose-50" data-testid={`product-image-delete-${img.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          );
        })}
        <button type="button" disabled={busy} onClick={() => input.current?.click()} data-testid="product-image-upload-btn"
          className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-border text-xs text-muted-foreground transition-colors hover:border-primary disabled:opacity-60">
          <Upload className="h-4 w-4" />{busy ? "Uploading…" : "Add images"}
        </button>
      </div>
      <input ref={input} type="file" multiple accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files)} data-testid="product-image-upload-input" />
      <p className="text-xs text-muted-foreground">JPG, PNG or WEBP up to 10MB each. Click the star to choose the main image.</p>
    </div>
  );
}
